import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import {FaFileAlt, FaFileCsv, FaFileExcel, FaFilePdf, FaFilePowerpoint, FaFileWord} from 'react-icons/fa';
import {CloseCircle, DirectInbox, Document} from 'iconsax-react';

import {DropzoneType} from 'configs/themeConfig';
import {getDropzoneData} from 'utils';
import {CustomFile, FilePreviewProps} from 'types';
import AppIconButton from 'components/buttons/AppIconButton';

export const FilesPreview: React.FC<FilePreviewProps> = ({
    showList = false,
    files,
    onRemove,
    type,
    previewSize = 80,
}) => {
    const hasFile = files.length > 0;

    const handleDownload = async (file: File) => {
        try {
            const url =
                typeof file !== 'string' && (file as any).preview.includes('blob')
                    ? URL.createObjectURL(file)
                    : `${process.env.REACT_APP_API_BASE_URL}/${(file as CustomFile).name}`;

            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = file.name;
            link.click();
            link.remove();

            if (url.startsWith('blob:')) URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    const openInNewTab = (url: string) => window.open(url, '_blank');

    const renderFileIcon = (type = '') => {
        if (type.includes('pdf')) return <FaFilePdf style={iconStyle('#d9534f')} />;
        if (type.includes('word') || type.includes('doc') || type.includes('docx'))
            return <FaFileWord style={iconStyle('#337ab7')} />;
        if (type.includes('excel') || type.includes('xls') || type.includes('xlsx'))
            return <FaFileExcel style={iconStyle('#5cb85c')} />;
        if (type.includes('csv') || type.includes('octet-stream')) return <FaFileCsv style={iconStyle('#5cb85c')} />;
        if (type.includes('ppt') || type.includes('pptx')) return <FaFilePowerpoint style={iconStyle('#f0ad4e')} />;
        return <FaFileAlt style={iconStyle('#6c757d')} />;
    };

    const iconStyle = (color: string) => ({
        width: 50,
        height: 50,
        cursor: 'pointer',
        color,
    });

    return (
        <List
            disablePadding
            sx={{
                ...(hasFile && type !== DropzoneType.STANDARD && {my: 1}),
                ...(type === DropzoneType.STANDARD && {width: 'calc(100% - 84px)'}),
            }}
        >
            {files.map((file, index) => {
                const {key, name, size, preview, type: mimeType} = getDropzoneData(file, index);

                if (showList) {
                    const isImage = mimeType?.includes('image');

                    return (
                        <ListItem
                            key={key}
                            sx={{
                                p: 0,
                                m: 0.5,
                                width: type === DropzoneType.STANDARD ? 64 : previewSize,
                                height: type === DropzoneType.STANDARD ? 64 : previewSize,
                                borderRadius: 1.25,
                                position: 'relative',
                                display: 'inline-flex',
                                border: '1px solid',
                                borderColor: 'divider',
                                overflow: 'hidden',
                            }}
                            onClick={e => {
                                e.stopPropagation();
                                const url =
                                    typeof file !== 'string' && (file as any).preview.includes('blob')
                                        ? URL.createObjectURL(file)
                                        : `${process.env.REACT_APP_API_BASE_URL}/${(file as CustomFile).name}`;
                                openInNewTab(url);
                            }}
                        >
                            {isImage && preview ? (
                                <img src={preview} alt={preview} style={{width: 50, height: 50, cursor: 'pointer'}} />
                            ) : (
                                renderFileIcon(mimeType)
                            )}

                            {!isImage && (
                                <AppIconButton
                                    onClick={e => {
                                        e.stopPropagation();
                                        typeof file !== 'string' && handleDownload(file);
                                    }}
                                    sx={downloadButtonStyle}
                                >
                                    <DirectInbox variant="Bold" />
                                </AppIconButton>
                            )}

                            {onRemove && (
                                <AppIconButton
                                    onClick={e => {
                                        e.stopPropagation();
                                        onRemove(file);
                                    }}
                                    sx={removeButtonStyle}
                                >
                                    <CloseCircle variant="Bold" />
                                </AppIconButton>
                            )}
                        </ListItem>
                    );
                }

                return (
                    <ListItem
                        key={key}
                        sx={{
                            my: 1,
                            px: 2,
                            py: 0.75,
                            borderRadius: 0.75,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Document variant="Bold" style={{width: 30, height: 30, fontSize: '1.15rem', marginRight: 4}} />
                        <ListItemText
                            primary={typeof file === 'string' ? file : name}
                            secondary={typeof file === 'string' ? '' : size}
                            primaryTypographyProps={{variant: 'subtitle2'}}
                            secondaryTypographyProps={{variant: 'caption'}}
                        />
                        {onRemove && (
                            <AppIconButton edge="end" size="small" onClick={() => onRemove(file)}>
                                <CloseCircle variant="Bold" style={{fontSize: '1.15rem'}} />
                            </AppIconButton>
                        )}
                    </ListItem>
                );
            })}
        </List>
    );
};

const downloadButtonStyle = {
    fontSize: '0.875rem',
    bgcolor: 'background.paper',
    p: 0,
    width: 'auto',
    height: 'auto',
    top: 2,
    right: 22,
    position: 'absolute',
};

const removeButtonStyle = {
    fontSize: '0.875rem',
    bgcolor: 'background.paper',
    p: 0,
    width: 'auto',
    height: 'auto',
    top: 2,
    right: 2,
    position: 'absolute',
};

export default FilesPreview;
